import React, { useState, useEffect, useRef } from 'react'
import styles from './style.module.scss'
import { useDispatch } from 'react-redux'
import { snackbarActions } from '@Root/store'
import { API } from '@Root/API'
import { all, errorMessage } from '@Root/helpers'
import { SectionWrapper, FormWrapper } from '@Root/HOCs'
import { Spinner, InputLabel, TextInput, Editor, PageTitle, BackButton } from '@Root/components'
import { useHistory, useParams } from 'react-router-dom'
import { useToggle } from '@Root/hooks'
import { generateStyleConfig } from '@Root/pages/Home/Admin/lib'

export const FormEmails = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { id: formId } = useParams()
  const styleConfig = generateStyleConfig(true, true, true)

  const [formName, setFormName] = useState('')
  const [emailId, setEmailId] = useState(null)
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('<p></p>')
  const [initialData, setInitialData] = useState({})
  const [isShown, setIsShown] = useState(false)
  const [isEditable, toggleEditable] = useToggle()
  const [isSpinning, setIsSpinning] = useState(false)
  const [isNew, setIsNew] = useState(false)
  const editorRef = useRef()

  const onError = error => {
    dispatch(snackbarActions.setSnackbar({ text: errorMessage(error), isError: true }))
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [formRes, emailsRes] = await Promise.all([
          API.cognitoForms.getFormById(formId),
          API.cognitoForms.getFormEmails({ cognito_form_id: formId }),
        ])

        const form = formRes.data?.data || formRes.data
        setFormName(form.form_name || '')

        const emails = emailsRes.data?.data || emailsRes.data || []
        const email = emails[0]

        if (email) {
          setEmailId(email.id)
          setSubject(email.subject || '')
          setBody(email.body || '<p></p>')
          setInitialData({
            subject: email.subject || '',
            body: email.body || '<p></p>',
          })
          setIsNew(false)
        } else {
          setIsNew(true)
          toggleEditable()
        }
      } catch (error) {
        onError(error)
        history.push('/home/forms')
      }
      setIsShown(true)
    }
    fetchData()
  }, [formId])

  const resetData = () => {
    if (isNew) return
    setSubject(initialData.subject)
    setBody(initialData.body)
    editorRef.current?.setEditorState(initialData.body)
  }

  const saveTemplate = async () => {
    try {
      setIsSpinning(true)

      if (isNew) {
        const { data } = await API.cognitoForms.createFormEmail({
          cognito_form_id: parseInt(formId),
          subject,
          body,
        })
        const created = data?.data || data
        setEmailId(created.id)
        setIsNew(false)
      } else {
        await API.cognitoForms.updateFormEmail(emailId, {
          subject,
          body,
        })
      }

      setInitialData({ subject, body })
      toggleEditable()
    } catch (error) {
      onError(error)
    }
    setIsSpinning(false)
  }

  const handleCancel = () => {
    if (isNew) {
      history.push('/home/forms')
      return
    }
    all(
      () => resetData(),
      () => toggleEditable()
    )
  }

  const readTitle = `Form Email: ${formName}`
  const editTitle = isNew ? `Create Form Email: ${formName}` : `Edit Form Email: ${formName}`

  return (
    <div className={styles.wrapper}>
      <PageTitle title={isEditable ? editTitle : readTitle} style={styleConfig.pageTitle} />
      <div className={styles.header}>
        <BackButton text='Back to Forms' clickHandler={() => history.push('/home/forms')} />
      </div>
      {isShown ? (
        <SectionWrapper>
          <FormWrapper
            buttons={isEditable ? ['cancel', 'save'] : []}
            buttonsAreDisabled={isSpinning}
            isSpinning={isSpinning}
            clickCancelButtonHandler={handleCancel}
            clickSaveButtonHandler={saveTemplate}
            isEditable={isEditable}
            canEdit={true}
            readTitle={readTitle}
            editTitle={editTitle}
            clickToggleEditModeButtonHandler={toggleEditable}
            idForm={`form_email_${formId}`}
            isSuccess={isSpinning && !isEditable}
          >
            <div className={styles.content}>
              <div className={styles.variables}>
                <div className={styles.formName}>Form: {formName}</div>
              </div>
              <div className={styles.template}>
                <InputLabel text='Subject' />
                <TextInput
                  classNames={!isEditable ? ['borderless'] : []}
                  style={{ marginBottom: 20, width: 756 }}
                  value={subject}
                  changeHandler={value => setSubject(value)}
                  isDisabled={!isEditable}
                />
                <InputLabel text='Body' />
                <Editor
                  className={styles.editor}
                  ref={editorRef}
                  value={body}
                  changeHandler={value => setBody(value)}
                  isDisabled={!isEditable}
                />
              </div>
            </div>
          </FormWrapper>
        </SectionWrapper>
      ) : (
        <Spinner style={{ position: 'absolute' }} />
      )}
    </div>
  )
}
